/**
 * Drives Horizon's native variant picker from a custom swatch UI. Never
 * duplicates variant/price/availability logic — it locates the real picker
 * input for the option (button/radio style matches `data-option-name`,
 * dropdown style matches `options[<name>]`) and dispatches the same `change`
 * event a real user interaction would. The swatch's own selected state is
 * then re-rendered server-side via cr-product-sync.js.
 */
class CrFinishSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.cr-finish-swatches__swatch');
    if (!button) return;

    const optionName = this.dataset.optionName ?? '';
    const optionValue = button.dataset.optionValue ?? '';
    const scope = this.closest('.shopify-section') ?? document;
    const picker = scope.querySelector('variant-picker') ?? scope;

    const radio = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).find(
      (input) => input.dataset.optionName === optionName && input.value === optionValue
    );
    if (radio instanceof HTMLInputElement) {
      if (radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.#markPressed(button);
      return;
    }

    const select = picker.querySelector(`select[name="${CSS.escape(`options[${optionName}]`)}"]`);
    if (select instanceof HTMLSelectElement && Array.from(select.options).some((o) => o.value === optionValue)) {
      select.value = optionValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#markPressed(button);
    }
  };

  /** Immediate feedback; the server re-render confirms it. */
  #markPressed(button) {
    this.querySelectorAll('.cr-finish-swatches__swatch').forEach((swatch) => {
      const pressed = swatch === button;
      swatch.setAttribute('aria-pressed', String(pressed));
      swatch.classList.toggle('cr-finish-swatches__swatch--selected', pressed);
    });
    const label = this.querySelector('.cr-finish-swatches__selected-label');
    if (label) label.textContent = button.dataset.optionValue ?? '';
  }
}

if (!customElements.get('cr-finish-swatches')) {
  customElements.define('cr-finish-swatches', CrFinishSwatches);
}
